import React, { useMemo } from "react";
import AlertaGenerador from "../../ui/AlertaGenerador";
import Generador from "../../../../assets/img/generador.png";
import SinPresion from "../../../../assets/img/sin_presion.png";
import SinSenalGenerador from "../../../../assets/img/sin_senal_Generador.png";
import Flujometro from "./FlujometroIcon.png";
import {
  generatorIsOn,
  isTekFish,
  outputsAreInconsistent,
  resolveRunOrder,
} from "../../../../utils/generatorOutputs";
import "./FooterNanoox.css";

const isValidNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value) && value >= 0;

const translateText = (translate, key, fallback) => {
  const value = typeof translate === "function" ? translate(key) : undefined;
  return value && value !== key ? value : fallback;
};

function resolveTekFish(item, translate) {
  const statuses = Array.isArray(item?.connection_status) ? item.connection_status : [];
  const commOk = statuses.includes(true);

  if (!commOk) {
    return {
      message: translateText(translate, "noComm", "Sin comunicacion"),
      alt: "no-comm",
    };
  }

  if (item?.falla_generador_tekfish) {
    return {
      message: translateText(translate, "alerta_generador", "Alarma en Generador"),
      alt: "alarm",
    };
  }

  switch (item?.digital_outputs_state) {
    case "10":
      return {
        message: translateText(translate, "Señal_on_enviada", "Senal de Encendido Enviada"),
        alt: "on-signal",
      };
    case "01":
      return {
        message: translateText(translate, "Señal_off_enviada", "Senal de Apagado Enviada"),
        alt: "off",
      };
    default:
      return {
        message: translateText(translate, "unknown_status", "Estado desconocido"),
        alt: "unknown",
      };
  }
}

function defaultResolve(item, translate) {
  if (isTekFish(item)) {
    return resolveTekFish(item, translate);
  }

  const state = String(item?.STATE || "").toUpperCase();
  const statuses = Array.isArray(item?.connection_status) ? item.connection_status : [];
  const commOk = statuses.includes(true);

  if (!commOk) {
    return { message: translateText(translate, "noComm", "Sin comunicacion"), alt: "no-comm" };
  }

  const outsOn = generatorIsOn(item);
  const order = resolveRunOrder(item);

  // La inconsistencia solo se declara con control por falla false explicito.
  // "110" y "011" son estados validos del enclavamiento y ya no levantan el
  // banner "Error Controlando Generador".
  const outsBad =
    outputsAreInconsistent(item?.digital_outputs_state, item?.control_generator_back) ||
    (order !== null && outsOn !== null && order !== outsOn);

  if (outsBad) {
    return {
      message: translateText(translate, "mismatch", "Inconsistencia de salidas"),
      alt: "mismatch",
    };
  }

  if (state === "HOLD_BEFORE_OFF") {
    return { message: translateText(translate, "shuttingDown", "Apagando..."), alt: "hold-before-off" };
  }

  if (state === "WARMUP") {
    return { message: translateText(translate, "warming", "Calentando"), alt: "warming" };
  }

  if (state === "IS_ON" && outsOn === true) {
    return { message: translateText(translate, "encendido", "Encendido"), alt: "on" };
  }

  if (order === false && outsOn === false) {
    return { message: translateText(translate, "apagado", "Apagado"), alt: "off" };
  }

  return { message: translateText(translate, "idle", "En espera"), alt: "idle" };
}

function resolveGenerator(item, translate) {
  if (!item || typeof item !== "object") {
    return { message: translateText(translate, "invalid_data", "Datos no validos"), alt: "invalid" };
  }

  if ("plc_status" in item) {
    if (item.plc_status === 100) {
      return { message: translateText(translate, "alerta_generador", "Alarma en Generador"), alt: "alarm" };
    }

    if (item.plc_status === -1) {
      return {
        message: translateText(translate, "sin_comunicacion_generador", "Sin comunicacion con Generador"),
        alt: "no-comm",
      };
    }

    return {
      message: translateText(translate, "sin_informacion_generador", "Sin Informacion Generador"),
      alt: "off",
    };
  }

  if ("hf_status" in item) {
    if (item.hf_status === -2) {
      return {
        message: translateText(translate, "sin_comunicacion_generador", "Sin comunicacion con Generador"),
        alt: "no-comm",
      };
    }

    return {
      message: `${translateText(translate, "status", "Estado")} HF ${item.hf_status}`,
      alt: "unknown",
    };
  }

  return defaultResolve(item, translate);
}

export default function FooterNanoox({
  modulos,
  umbrales,
  generadores,
  translate,
  TemperaturaGlobal,
  AcumuladoO2Global,
}) {
  const hasAnyPressureHysteresis = useMemo(
    () =>
      (modulos ?? []).some((modulo) =>
        (modulo?.cans ?? []).some((can) => can?.presion_histeresis === true)
      ),
    [modulos]
  );

  const normalizedGenerators = useMemo(() => {
    if (Array.isArray(generadores)) {
      if (generadores.every((entry) => Array.isArray(entry) && entry.length === 2)) {
        return generadores;
      }

      return generadores.map((payload, index) => [`gen${index + 1}`, payload]);
    }

    if (generadores && typeof generadores === "object") {
      const keys = Object.keys(generadores);

      if (keys.length > 1 && !("hf_status" in generadores) && !("plc_status" in generadores)) {
        return Object.entries(generadores);
      }

      return [["gen1", generadores]];
    }

    return [];
  }, [generadores]);

  const alertConfigs = [];

  if (hasAnyPressureHysteresis) {
    alertConfigs.push({
      key: "pressure",
      logo: SinPresion,
      title: `${translateText(translate, "sin_presion", "Sin Presion de")} O2** ${translateText(translate, "en_la_linea", "en la linea")}`,
      iluminar: false,
    });
  }

  normalizedGenerators.forEach(([id, payload]) => {
    const visual = resolveGenerator(payload, translate);
    const idLabel = id.replace(/\D/g, "") || id;

    if (visual.alt === "no-comm") {
      alertConfigs.push({
        key: `gen-${id}-comm`,
        logo: SinSenalGenerador,
        title: `${translateText(translate, "sin_com", "Sin Comunicacion")}** ${translateText(translate, "con_generator", "con Generador")} ${idLabel}`,
      });
    }

    if (visual.alt === "mismatch") {
      alertConfigs.push({
        key: `gen-${id}-mismatch`,
        logo: Generador,
        title: `${translateText(translate, "error_controlando", "Error Controlando")}** ${translateText(translate, "generator", "Generador")} ${idLabel}`,
      });
    }

    if (visual.alt === "alarm") {
      alertConfigs.push({
        key: `gen-${id}-alarm`,
        logo: Generador,
        title: `${translateText(translate, "alerta_generador", "Alarma en Generador")}** ${translateText(translate, "generator", "Generador")} ${idLabel}`,
      });
    }
  });

  return (
    <div className="footerbar">
      <div className="footer-alerts">
        {alertConfigs.map((config) => (
          <AlertaGenerador
            key={config.key}
            logo={config.logo}
            title={config.title}
            iluminar={config.iluminar}
          />
        ))}
      </div>

      <div className="footer-umbrales-container">
        <div className="footer-umbrales">
          <div className="footer-umbrales-title">
            <h3>
              {translateText(translate, "umbral", "Umbral de Inyeccion O2")} <span>[mg/L]</span>
            </h3>
          </div>
          <div className="footer-umbrales-values">
            <span className="chipMin">
              {translateText(translate, "min", "Min")}:{umbrales?.setpoin_on || umbrales?.[0]?.Min || ""}
            </span>
            <span className="chipMin">
              {translateText(translate, "max", "Max")}: {umbrales?.setpoin_off || umbrales?.[0]?.Max || ""}
            </span>
          </div>
        </div>
      </div>

      <div className="footer-temp">
        <TemperatureCard TemperaturaGlobal={TemperaturaGlobal} translate={translate} />
      </div>

      {AcumuladoO2Global?.global > 0 && (
        <div className="footer-temp">
          <AccumulatedO2Card FlujometroGlobal={AcumuladoO2Global} iconSrc={Flujometro} translate={translate} />
        </div>
      )}
    </div>
  );
}

function TemperatureCard({ TemperaturaGlobal, translate }) {
  const globalNumber =
    typeof TemperaturaGlobal?.global === "number" ? TemperaturaGlobal.global : null;

  const cages = useMemo(() => {
    const list = [];

    (TemperaturaGlobal?.porModulo ?? []).forEach((module) => {
      (module?.jaulas ?? []).forEach((cage) => {
        if (typeof cage.temperatura === "number") {
          list.push({
            alias: cage.alias || translateText(translate, "no_alias", "Sin alias"),
            temperatura: cage.temperatura,
          });
        }
      });
    });

    return list;
  }, [TemperaturaGlobal, translate]);

  const percentage = useMemo(() => {
    if (globalNumber == null) return 0;
    const max = 30;
    const clamped = Math.max(0, Math.min(max, globalNumber));
    return Math.round((clamped / max) * 100);
  }, [globalNumber]);

  const globalText = globalNumber == null ? "—" : `${globalNumber.toFixed(2)} °C`;

  return (
    <div className="cardm">
      <div className="card-temp" style={{ width: "8vw" }}>
        <ThermometerIcon widthVW={3} heightVW={5} strokeWidthVW={0.05} value={percentage} />
        <div>
          <div className="mainHead">{translateText(translate, "temperatura", "Temperatura")}</div>
          <div className="main">{globalText}</div>
        </div>
      </div>

      <div className="card2">
        <div className="upper">
          <div className="tooltip-title">{translateText(translate, "temperatura", "Temperatura")}</div>
        </div>
        <div className="lower list">
          {cages.length === 0 ? (
            <div className="row">{translateText(translate, "no_data", "Sin datos")}</div>
          ) : (
            cages.map((cage, index) => (
              <div className="row" key={`${cage.alias}-${index}`}>
                <span className="alias">{cage.alias}</span>
                <span className="temp">{cage.temperatura.toFixed(2)} °C</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function AccumulatedO2Card({ FlujometroGlobal, iconSrc, translate }) {
  const flowNumber = useMemo(() => {
    if (typeof FlujometroGlobal === "number") return FlujometroGlobal;
    if (typeof FlujometroGlobal?.global === "number") return FlujometroGlobal.global;
    return null;
  }, [FlujometroGlobal]);

  const cages = useMemo(() => {
    const list = [];

    (FlujometroGlobal?.porModulo ?? []).forEach((module) => {
      (module?.jaulas ?? []).forEach((cage) => {
        if (typeof cage.acumulado === "number") {
          list.push({
            alias: cage.alias || translateText(translate, "no_alias", "Sin alias"),
            acumulado: cage.acumulado,
          });
        }
      });
    });

    return list;
  }, [FlujometroGlobal, translate]);

  const formatAccumulator = (value) => {
    if (!isValidNumber(value)) return { value: "--", unit: "" };
    const amount = Number(value);

    if (amount >= 100000) {
      return { value: (amount / 1000).toFixed(2), unit: "Ton O2" };
    }

    if (amount >= 1000) {
      return { value: Math.floor(amount).toLocaleString("es-CL"), unit: "kg O2" };
    }

    return { value: amount.toFixed(2), unit: "kg O2" };
  };

  const formatted = flowNumber == null ? null : formatAccumulator(flowNumber);

  return (
    <div className="cardm ui-temp">
      <div className="card-temp ui-chip" style={{ width: "9.5vw", marginLeft: "-.7vw" }}>
        <img src={iconSrc} alt="Flujometro" className="ui-flow-icon" width="auto" height="auto" />

        <div className="column_main">
          <div className="mainHead ui-chip-value">
            {translateText(translate, "accumulated_o2", "O2 Acumulado")}
          </div>
          <div className="main ui-chip-value">
            {formatted ? `${formatted.value} ${formatted.unit}` : "—"}
          </div>
        </div>
      </div>

      <div className="card2">
        <div className="upper">
          <div className="tooltip-title">
            {translateText(translate, "accumulated_o2", "O2 Acumulado")}
          </div>
        </div>
        <div className="lower list">
          {cages.length === 0 ? (
            <div className="row">{translateText(translate, "no_data", "Sin datos")}</div>
          ) : (
            cages.map((cage, index) => {
              const { value, unit } = formatAccumulator(cage.acumulado);
              return (
                <div className="row" key={`${cage.alias}-${index}`}>
                  <span className="alias">{cage.alias}</span>
                  <span className="temp">
                    {value} {unit}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function ThermometerIcon({
  widthVW = 8,
  heightVW = 7,
  value = 75,
  color = "#b95b1d",
  strokeWidthVW = 0.12,
  title = "Termometro",
  ...props
}) {
  const currentValue = Math.max(0, Math.min(100, value));
  const tubeYTop = 3;
  const tubeYBottom = 15;
  const tubeHeight = tubeYBottom - tubeYTop;
  const mercuryHeight = (currentValue / 100) * tubeHeight;
  const mercuryY = tubeYBottom - mercuryHeight;
  const strokeWidth = `${strokeWidthVW}vw`;

  return (
    <svg
      width={`${widthVW}vw`}
      height={`${heightVW}vw`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${title}: ${currentValue}%`}
      {...props}
    >
      <rect
        x={10.5}
        y={3}
        width={3}
        height={12}
        rx={1.5}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      <circle
        cx={12}
        cy={18}
        r={4}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill="none"
        vectorEffect="non-scaling-stroke"
      />

      <rect x={11} y={14.5} width={2} height={2} fill="white" />
      <circle cx={12} cy={18} r={3.2} fill={color} />
      <rect x={11.1} y={mercuryY} width={1.8} height={mercuryHeight} fill={color} />

      <g
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        opacity={0.65}
        vectorEffect="non-scaling-stroke"
      >
        <line x1={15.5} y1={5} x2={17.5} y2={5} />
        <line x1={15.5} y1={8} x2={17} y2={8} />
        <line x1={15.5} y1={11} x2={17} y2={11} />
        <line x1={15.5} y1={14} x2={17.5} y2={14} />
      </g>
    </svg>
  );
}
